package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.AudienceCardStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Split Converge
@Composable
private fun Modifier.splitConverge(isVisible: Boolean, isLeft: Boolean): Modifier {
    val startX = if (isLeft) (-100).px else 100.px
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else startX).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun ProductAudienceRoutingSection(data: ProductAudienceRoutingSectionData = defaultProductAudienceRoutingData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-audience-routing"
    var isVisible by remember { mutableStateOf(false) }

    if (data.routes.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    SimpleGrid(
                        numColumns = numColumns(base = 1, lg = 2),
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(40.px)
                    ) {
                        data.routes.forEachIndexed { index, route ->
                            key(route.title) {
                                AudienceCard(
                                    route.title,
                                    route.tagline,
                                    route.description,
                                    route.buttonText,
                                    route.linkPath,
                                    Modifier.splitConverge(isVisible, isLeft = index % 2 == 0)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AudienceCard(
    title: String,
    tagline: String,
    description: String,
    buttonText: String,
    linkPath: String,
    modifier: Modifier = Modifier
) {
    val palette = ColorMode.current.toSitePalette()
    Box(
        modifier = AudienceCardStyle.toModifier().then(modifier).fillMaxWidth().minHeight(500.px).styleModifier {
            property(
                "background", "linear-gradient(135deg, ${palette.brand.primaryHover} 0%, ${palette.brand.primary} 100%)"
            )
        }) {
        Column(
            modifier = Modifier.fillMaxSize().padding(40.px),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.Start
        ) {
            Column {
                if (title.isNotBlank()) {
                    Div(
                        attrs = Modifier.color(palette.brand.primarySoft).fontWeight(FontWeight.Bold).fontSize(20.px)
                            .letterSpacing(1.px).margin(bottom = 12.px).toAttrs {
                                ref { htmlElement -> htmlElement.innerHTML = title; onDispose {} }
                            })
                }
                if (tagline.isNotBlank()) {
                    Div(
                        attrs = Modifier.color(Colors.White).fontWeight(FontWeight.ExtraBold).fontSize(28.px)
                            .lineHeight(1.3).margin(bottom = 20.px).toAttrs {
                                ref { htmlElement -> htmlElement.innerHTML = tagline; onDispose {} }
                            })
                }
                if (description.isNotBlank()) Div(
                    attrs = Modifier.color(Colors.White).fontSize(18.px).lineHeight(1.6)
                    .styleModifier {
                        property("display", "-webkit-box"); property(
                        "-webkit-line-clamp", "5"
                    ); property("-webkit-box-orient", "vertical"); property("overflow", "hidden")
                    }.toAttrs { ref { htmlElement -> htmlElement.innerHTML = description; onDispose {} } })
            }
            if (buttonText.isNotBlank() && linkPath.isNotBlank()) {
                Link(
                    path = linkPath,
                    modifier = Modifier.textDecorationLine(com.varabyte.kobweb.compose.css.TextDecorationLine.None)
                ) {
                    Box(
                        modifier = Modifier.backgroundColor(Colors.White).color(palette.brand.primary)
                            .padding(leftRight = 24.px, topBottom = 12.px).borderRadius(8.px)
                            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer), contentAlignment = Alignment.Center
                    ) {
                        SpanText(text = buttonText, modifier = Modifier.fontSize(20.px).fontWeight(FontWeight.SemiBold))
                    }
                }
            }
        }
    }
}

data class AudienceRouteData(
    val title: String, val tagline: String, val description: String, val buttonText: String, val linkPath: String
)

data class ProductAudienceRoutingSectionData(
    val sectionId: String? = null, val sectionHeader: String, val routes: List<AudienceRouteData>
)

val defaultProductAudienceRoutingData = ProductAudienceRoutingSectionData(
    sectionId = "product-audience-routing",
    sectionHeader = "MyHand<sup>®</sup> BENEFITS EVERY STAKEHOLDER",
    routes = listOf(
        AudienceRouteData(
            "For Clinicians",
            "Transform Your Practice, Enhance Patient Outcomes.",
            "Includes a built-in assessment protocol to streamline evaluations, along with pre-programmed and customizable protocols to adapt to individual therapy needs. Advanced analytics and reporting tools provide real-time insights into progress.",
            "Clinician Resources",
            "/clinicians"
        ), AudienceRouteData(
            "For Patients & Caregivers",
            "With MyHand<sup>®</sup>, it's in your hands!",
            "Empowers users to reclaim their independence and enhance their quality of life. Developed and tested with patients experiencing various levels of hand function impairment, built on a unique, smart digital therapy platform.",
            "Patient Journey",
            "/patients"
        )
    ) // Your existing payload
)